import { Flex } from '@/ui/Flex/Flex';
import HorizontalDivider from '@/ui/HorizontalDivider';
import { T } from '@/ui/Text/Text';
import { useIntl } from 'react-intl';
import {
  IconBrightness,
  IconColor,
  IconHometimer,
  IconIdentifier,
  IconLEDSecondary,
} from '@/icons';
import { Input } from '@/ui/Input/Input';
import CustomSelect, { MultiSelect } from '@/ui/Select/Select';
import CustomSwitch from '@/ui/Switch/Switch';
import { ActionToggleLEDNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType, SupportedColors } from '../types';
import { generateLEDOptions } from '../../util/generateSettingsOptions';

function ToggleLEDSettings({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  settings,
  selectedNode,
  updateNodeData,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: ActionToggleLEDNodeType;
  updateNodeData: (id: string, newData: any) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();

  const faqs = [
    {
      question: intl.formatMessage({
        defaultMessage: 'How does the Toggle LED work?',
        id: '6NmEVL',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Toggle LED allows you to turn on/off the selected LED, depending on its current state. If the LED is off it will turn on, if it is on it will turn off.',
        id: 'GfLAx1',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What is Fade In?',
        id: 'l5m8j5',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Fade In describes the way the LED will turn on. You can define the brightness, colour and duration of the fade in effect. The LED will then smoothly transition to the defined brightness and colour over the specified duration.',
        id: 'cMAYtM',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What is Pulse?',
        id: 'd2a9Uq',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Pulse describes another way of the LED turning on. You can select between Pulse or Fade In. Pulse allows the LED to switch between different colors and brightness levels. You can define two states for the LED, each with its own brightness and color. The LED will then smoothly transition between these two states over the specified duration.',
        id: '26rNg6',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'How does the Fade Out Duration work?',
        id: 'jgSPzE',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Fade Out Duration defines how long it takes for the LED to turn off after the action is triggered. You can set this duration in seconds, and the LED will gradually fade out over that time.',
        id: 'ReWOjl',
      }),
    },
  ];

  const relevantData = selectedNode.data.options;

  const ledOptions = generateLEDOptions(settings.leds);

  return (
    <StyledSettings data-testid="action-toggleLED-settings">
      <Flex align="center" gap={8}>
        <IconLEDSecondary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({
              defaultMessage: 'Turn On/Off LED',
              id: '28El6X',
            })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={16}>
          <Flex direction="column" gap={8}>
            <MultiSelect
              disabled={disabled ?? false}
              required
              title={intl.formatMessage({
                defaultMessage: 'LED(s)',
                id: 'c8hZEV',
              })}
              icon={<IconIdentifier />}
              placeholder={intl.formatMessage({
                defaultMessage: 'Select LED(s)',
                id: 'kORkwT',
              })}
              size="small"
              options={ledOptions}
              onChange={(e) => {
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: {
                    ...relevantData,
                    leds: e ? e.map((item) => item.value) : null,
                  },
                });
              }}
              value={
                Array.isArray(relevantData.leds) && relevantData.leds.length > 0
                  ? relevantData.leds.map((led) => ({
                      label: led.name,
                      value: {
                        channel: led.channel,
                        identifier: led.identifier,
                        name: led.name,
                      },
                    }))
                  : null
              }
            />
          </Flex>
          <HorizontalDivider />
          <Flex justify="space-between" align="center">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Fade In',
                id: '/dQrVa',
              })}
            </T>
            <CustomSwitch
              disabled={disabled ?? false}
              checked={!!relevantData.ramp}
              onChange={() =>
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: {
                    ...relevantData,
                    ramp: true,
                    pulse: false,
                  },
                })
              }
            />
          </Flex>
          {!!relevantData.ramp && (
            <Flex direction="column" gap={8}>
              <Flex justify="space-between" align="center">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Brightness',
                    id: 'VPtfWY',
                  })}
                </T>
                <Input
                  disabled={disabled ?? false}
                  type="number"
                  size="small"
                  value={
                    relevantData.rampOptions.brightness !== undefined &&
                    relevantData.rampOptions.brightness !== null
                      ? relevantData.rampOptions.brightness
                      : 99
                  }
                  onBlur={(e) => {
                    const value =
                      e === '' || e === undefined || e === null
                        ? 99
                        : Number(e);
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: {
                        ...relevantData,
                        rampOptions: {
                          ...relevantData.rampOptions,
                          brightness: value,
                        },
                      },
                    });
                  }}
                  onChange={() => {}}
                  stepSize={0.1}
                  min={0}
                  max={99}
                  placeholder="99"
                  icon={<IconBrightness />}
                />
              </Flex>

              <Flex justify="space-between" align="center">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Colour',
                    id: '+e8vAT',
                  })}
                </T>
                <CustomSelect
                  disabled={disabled ?? false}
                  icon={<IconColor />}
                  isSearchable={false}
                  size="small"
                  placeholder="Select Colour"
                  options={Object.entries(SupportedColors)
                    .filter(([key]) => isNaN(Number(key)))
                    .map(([colourName, colourNumber]) => ({
                      label: colourName,
                      value: colourNumber,
                    }))}
                  value={
                    relevantData.rampOptions.colour
                      ? {
                          label: Object.entries(SupportedColors).find(
                            ([, value]) =>
                              value === relevantData.rampOptions.colour,
                          )?.[0],
                          value: relevantData.rampOptions.colour,
                        }
                      : {
                          label: 'RED',
                          value: SupportedColors.RED,
                        }
                  }
                  onChange={(e) => {
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: {
                        ...relevantData,
                        rampOptions: {
                          ...relevantData.rampOptions,
                          colour: e?.value,
                        },
                      },
                    });
                  }}
                />
              </Flex>
              <Flex justify="space-between" align="center">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Fade In Duration (in seconds)',
                    id: '6hO88p',
                  })}
                </T>
                <Input
                  disabled={disabled ?? false}
                  type="number"
                  size="small"
                  value={
                    typeof relevantData.rampOptions.duration === 'number'
                      ? relevantData.rampOptions.duration / 10
                      : 10
                  }
                  onBlur={(e) => {
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: {
                        ...relevantData,
                        rampOptions: {
                          ...relevantData.rampOptions,
                          duration: Number(e) * 10,
                        },
                      },
                    });
                  }}
                  onChange={() => {}}
                  stepSize={0.1}
                  min={0}
                  max={99}
                  icon={<IconHometimer />}
                />
              </Flex>
            </Flex>
          )}
          <HorizontalDivider />
          <Flex justify="space-between" align="center">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Pulse',
                id: 'DTM010',
              })}
            </T>
            <CustomSwitch
              disabled={disabled ?? false}
              checked={!!relevantData.pulse}
              onChange={() =>
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: {
                    ...relevantData,
                    pulse: true,
                    ramp: false,
                  },
                })
              }
            />
          </Flex>
          {!!relevantData.pulse && (
            <Flex direction="column" gap={16}>
              <Flex direction="column" gap={8}>
                <Flex justify="space-between" align="center">
                  <T body1>
                    {intl.formatMessage({
                      defaultMessage: 'Brightness State One',
                      id: 'jyyX63',
                    })}
                  </T>
                  <Input
                    disabled={disabled ?? false}
                    type="number"
                    size="small"
                    value={
                      typeof relevantData.pulseOptions.brightnessStateOne ===
                      'number'
                        ? relevantData.pulseOptions.brightnessStateOne
                        : 99
                    }
                    onBlur={(e) => {
                      updateNodeData(selectedNode.id, {
                        ...selectedNode.data,
                        options: {
                          ...relevantData,
                          pulseOptions: {
                            ...relevantData.pulseOptions,
                            brightnessStateOne: Number(e),
                          },
                        },
                      });
                    }}
                    onChange={() => {}}
                    stepSize={0.1}
                    min={0}
                    max={99}
                    icon={<IconBrightness />}
                  />
                </Flex>

                <Flex justify="space-between" align="center">
                  <T body1>
                    {intl.formatMessage({
                      defaultMessage: 'Colour State One',
                      id: '0FhNKl',
                    })}
                  </T>
                  <CustomSelect
                    disabled={disabled ?? false}
                    icon={<IconColor />}
                    isSearchable={false}
                    size="small"
                    placeholder="Select Colour"
                    options={Object.entries(SupportedColors)
                      .filter(([key]) => isNaN(Number(key)))
                      .map(([colourName, colourNumber]) => ({
                        label: colourName,
                        value: colourNumber,
                      }))}
                    value={
                      relevantData.pulseOptions.colourStateOne
                        ? {
                            label: Object.entries(SupportedColors).find(
                              ([, value]) =>
                                value ===
                                relevantData.pulseOptions.colourStateOne,
                            )?.[0],
                            value: relevantData.pulseOptions.colourStateOne,
                          }
                        : {
                            label: 'RED',
                            value: SupportedColors.RED,
                          }
                    }
                    onChange={(e) => {
                      updateNodeData(selectedNode.id, {
                        ...selectedNode.data,
                        options: {
                          ...relevantData,
                          pulseOptions: {
                            ...relevantData.pulseOptions,
                            colourStateOne: e?.value,
                          },
                        },
                      });
                    }}
                  />
                </Flex>
              </Flex>
              <Flex direction="column" gap={8}>
                <Flex justify="space-between" align="center">
                  <T body1>
                    {intl.formatMessage({
                      defaultMessage: 'Brightness State Two',
                      id: '0fMLuD',
                    })}
                  </T>
                  <Input
                    disabled={disabled ?? false}
                    type="number"
                    size="small"
                    value={
                      typeof relevantData.pulseOptions.brightnessStateTwo ===
                      'number'
                        ? relevantData.pulseOptions.brightnessStateTwo
                        : 99
                    }
                    onBlur={(e) => {
                      updateNodeData(selectedNode.id, {
                        ...selectedNode.data,
                        options: {
                          ...relevantData,
                          pulseOptions: {
                            ...relevantData.pulseOptions,
                            brightnessStateTwo: Number(e),
                          },
                        },
                      });
                    }}
                    onChange={() => {}}
                    stepSize={0.1}
                    min={0}
                    max={99}
                    icon={<IconBrightness />}
                  />
                </Flex>

                <Flex justify="space-between" align="center">
                  <T body1>
                    {intl.formatMessage({
                      defaultMessage: 'Colour State Two',
                      id: 'dIX2b+',
                    })}
                  </T>
                  <CustomSelect
                    disabled={disabled ?? false}
                    icon={<IconColor />}
                    isSearchable={false}
                    size="small"
                    placeholder="Select Colour"
                    options={Object.entries(SupportedColors)
                      .filter(([key]) => isNaN(Number(key)))
                      .map(([colourName, colourNumber]) => ({
                        label: colourName,
                        value: colourNumber,
                      }))}
                    value={
                      relevantData.pulseOptions.colourStateOne
                        ? {
                            label: Object.entries(SupportedColors).find(
                              ([, value]) =>
                                value ===
                                relevantData.pulseOptions.colourStateTwo,
                            )?.[0],
                            value: relevantData.pulseOptions.colourStateTwo,
                          }
                        : {
                            label: 'RED',
                            value: SupportedColors.RED,
                          }
                    }
                    onChange={(e) => {
                      updateNodeData(selectedNode.id, {
                        ...selectedNode.data,
                        options: {
                          ...relevantData,
                          pulseOptions: {
                            ...relevantData.pulseOptions,
                            colourStateTwo: e?.value,
                          },
                        },
                      });
                    }}
                  />
                </Flex>
              </Flex>
              <Flex justify="space-between" align="center">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Fade In Duration (in seconds)',
                    id: '6hO88p',
                  })}
                </T>
                <Input
                  disabled={disabled ?? false}
                  type="number"
                  size="small"
                  value={
                    typeof relevantData.pulseOptions.rampDuration === 'number'
                      ? relevantData.pulseOptions.rampDuration / 10
                      : 10
                  }
                  onBlur={(e) => {
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: {
                        ...relevantData,
                        pulseOptions: {
                          ...relevantData.pulseOptions,
                          rampDuration: Number(e) * 10,
                        },
                      },
                    });
                  }}
                  onChange={() => {}}
                  stepSize={0.1}
                  min={0}
                  max={99}
                  icon={<IconHometimer />}
                />
              </Flex>
            </Flex>
          )}
          <HorizontalDivider />
          <Flex direction="column" gap={8}>
            <Flex justify="space-between" align="center">
              <T body1>
                {intl.formatMessage({
                  defaultMessage: 'Fade Out Duration (in seconds)',
                  id: 'EsUwrE',
                })}
              </T>
              <Input
                disabled={disabled ?? false}
                type="number"
                size="small"
                value={
                  typeof relevantData.off.rampDownDuration === 'number'
                    ? relevantData.off.rampDownDuration / 10
                    : 0
                }
                onBlur={(e) => {
                  updateNodeData(selectedNode.id, {
                    ...selectedNode.data,
                    options: {
                      ...relevantData,
                      off: {
                        ...relevantData.off,
                        rampDownDuration: Number(e) * 10,
                      },
                    },
                  });
                }}
                onChange={() => {}}
                stepSize={0.1}
                min={0}
                max={99}
                icon={<IconHometimer />}
              />
            </Flex>
          </Flex>
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>

      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}
export default ToggleLEDSettings;
